import { Theme } from '@emotion/react';
import useStylesheets from '@vromo/hooks/useStylesheets';
import useTheme from '@vromo/hooks/useTheme';
import { useStore } from '@vromo/mobx-react';
import { css } from 'emotion';
import { observer } from 'mobx-react-lite';
import React, { memo, useEffect, useRef, useState } from 'react';
import smoothscroll from 'smoothscroll-polyfill';
import { qaLabelTrackingLink } from '../../../common-qa-labels/tracking-link';
import TrackingLinkStore from '../TrackingLinkStore';
import SpatulaSignedJobView from '../WeDispatch/SpatulaSignedJobView.css';
import Widget from './Widget';
import ETADisplay from './components/ETADisplay/ETADisplay';
import MapWrapper from './components/MapWrapper/MapWrapper';
import OrderDetails from './components/OrderDetails/OrderDetails';
import OrderProgress from './components/OrderProgress';
import OrderProgressBar from './components/OrderProgress/components/OrderProgressBar';
import styles, { MOBILE_TOP_BANNER_HEIGHT } from './orderTracking.mobile.style';
import { UIStateInterface } from './useUIState';

smoothscroll.polyfill();

interface Props {
    host: string;
    auth: string;
    UIState: UIStateInterface;
}

const OrderTrackingMobile = observer(({ host, auth, UIState }: Props) => {
    const [hasMounted, setHasMounted] = useState<boolean>(false);
    const scrollRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<HTMLDivElement>(null);
    const fadeOutRef = useRef<HTMLDivElement>(null);

    useStylesheets(SpatulaSignedJobView);
    const campaignTheme = useTheme();

    const rootStore = useStore<TrackingLinkStore>('rootStore');
    const job = rootStore.jobs.first()!;
    const vehicle = rootStore.vehicles.first()!;
    const widgets = job?.campaign?.widgets || [];

    useEffect(() => setHasMounted(true), []);

    const bannerImage = job?.campaign?.logo?.url;
    const showWidgets = job.campaign?.promotion?.enabled;

    return (
        <div
            style={styles.container}
            className={css`
                overflow: scroll;
                -webkit-overflow-scrolling: touch;
                ::-webkit-scrollbar {
                    display: none;
                }
                scrollbar-width: none;
                -ms-overflow-style: none;
            `}>
            {!!bannerImage && (
                <div
                    style={{
                        ...styles.topBanner,
                        backgroundColor: campaignTheme.header || campaignTheme.background
                    }}>
                    <img
                        data-testid={qaLabelTrackingLink.headerLogo}
                        src={bannerImage}
                        style={styles.bannerImage}
                    />
                </div>
            )}

            <div
                ref={mapRef}
                style={{
                    ...styles.map,
                    marginTop: bannerImage ? MOBILE_TOP_BANNER_HEIGHT : 0
                }}>
                <div style={styles.mapContainer}>
                    {hasMounted && <MapWrapper />}
                    <div ref={fadeOutRef} style={styles.mapFader}></div>
                </div>
            </div>
            <div
                ref={scrollRef}
                style={{
                    ...styles.bottomSheet
                }}>
                <div style={styles.bottomSheetContent}>
                    <ETADisplay />
                    {!job.cancelled && <OrderProgressBar progress={UIState.progress} />}
                    <div style={styles.title}>{UIState.statusTitle}</div>
                    <div style={styles.statusDescription}>{UIState.statusDescription}</div>
                    <OrderProgress {...{ vehicle, host, auth }} stepIx={UIState.stepIx} />
                    <div style={styles.lineDivider} />
                    <OrderDetails />
                    <div style={styles.widgetContainer}>
                        {showWidgets && (
                            <div
                                className={css`
                                    width: 100%;
                                    max-width: 400px;
                                `}>
                                {widgets.map((widget, i) => (
                                    <div key={`widget-${i}`}>
                                        <Widget
                                            key={widget.id}
                                            theme={campaignTheme as Theme}
                                            {...{ widget, vehicle, job, host, auth }}
                                        />
                                        {i < widgets.length - 1 && (
                                            <div style={styles.lineDivider} />
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
});

export default memo(OrderTrackingMobile);
